import { memo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid2,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  AccountCircle,
  Home,
  Favorite,
  Logout,
  LocationOn,
  ArrowForward,
  LockReset,
} from "@mui/icons-material";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../Store/store";
import { logout } from "../../Redux/authReducer";
import { useLocation, useNavigate } from "react-router-dom";
import { googleLogout } from "@react-oauth/google";
import toast from "react-hot-toast";
import { useForgotPasswordMutation } from "../../Services/api.auth.slice";
import EmailPreferencesCard from "./EmailPreferencesCard";
import AccountPlanCard from "./AccountPlanCard";
import MessageDialog from "../../Components/MessageForm/MessageDialog";
import { useSiteConfig } from "../../Hooks/useSiteConfig";
import { TRADE_LABELS } from "../../Services/contractApi";

const MyAccount = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, wishlist } = useSelector(
    (state: RootState) => state?.auth || {}
  );

  const propertyCollections = user?.propertyCollections || [];
  const userEmail = user?.email || "User";
  // A Google account has no password until its owner chooses to set one. The
  // control is the same reset flow either way — only the wording differs, and
  // getting it wrong is what made this button read as broken. Default true so a
  // session that predates the API sending this field still says "Reset".
  const hasPassword = user?.hasPassword !== false;

  const [forgotPassword, { isLoading: isResetting }] = useForgotPasswordMutation();

  const handleResetPassword = async () => {
    if (!userEmail || userEmail === "User") {
      toast.error("Email not found");
      return;
    }
    try {
      await forgotPassword({ email: userEmail }).unwrap();
      toast.success("Password reset link sent to your email");
    } catch (error) {
      toast.error("Failed to send reset email. Please try again.");
    }
  };

  const handleLogout = () => {
    navigate("/");
    googleLogout();
    toast("Logged out successfully");
    dispatch(logout());
  };

  const handlePropertyClick = (property: any) => {
    const propertyId =
      property?.propertyId?.propertyId || property?.propertyId?._id;
    if (propertyId) {
      navigate(`/${propertyId}/property-id/${propertyId}`);
    }
  };

  const getPropertyAddress = (property: any) => {
    const address = property?.propertyId?.propertyInfo?.address;
    if (!address) return "Property";
    const fullAddress =
      `${address.street || ""} ${address.city || ""}, ${address.state || ""}`.trim();
    return fullAddress || "Property";
  };

  // "Request a service" flow from the homepage lands here with intent state.
  const location = useLocation();
  const [serviceCta, setServiceCta] = useState<"claim" | "select" | null>(null);
  const [serviceTrade, setServiceTrade] = useState<string>("");
  const [askOpen, setAskOpen] = useState(false);
  const { config: siteConfig } = useSiteConfig();
  useEffect(() => {
    const cta = (location.state as any)?.serviceCta;
    if (cta === "claim" || cta === "select") setServiceCta(cta);
    const trade = (location.state as any)?.serviceTrade;
    setServiceTrade(typeof trade === "string" ? trade : "");
  }, [location.state]);

  // The property dashboard's pro finder, with the requested trade's job form
  // already open.
  const goToHomeRecords = (property: any) => {
    const propertyId =
      property?.propertyId?.propertyId || property?.propertyId?._id;
    if (propertyId) {
      setServiceCta(null);
      const trade = serviceTrade ? `&trade=${encodeURIComponent(serviceTrade)}` : "";
      navigate(`/${propertyId}/property-id/${propertyId}?tab=find-a-pro${trade}`);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #42a5f5 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
            mt: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Avatar
              sx={{
                width: { xs: 64, md: 80 },
                height: { xs: 64, md: 80 },
                bgcolor: "rgba(255,255,255,0.2)",
              }}
            >
              <AccountCircle sx={{ fontSize: { xs: 48, md: 60 } }} />
            </Avatar>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                  lineHeight: 1.2,
                }}
              >
                My Account
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  opacity: 0.9,
                  mt: 1,
                }}
              >
                {userEmail}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 2, md: 4 },
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          <Grid2 container spacing={4}>
            {/* My Properties Section */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Home sx={{ color: "#fff", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={600}>
                        My Properties
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {propertyCollections.length} claimed{" "}
                        {propertyCollections.length === 1
                          ? "property"
                          : "properties"}
                      </Typography>
                    </Box>
                  </Box>

                  {propertyCollections.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {propertyCollections.map((property: any, idx: number) => (
                        <Box
                          key={property?.propertyId?._id || idx}
                          onClick={() => handlePropertyClick(property)}
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            backgroundColor: "#f5f5f5",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <LocationOn sx={{ color: "primary.main" }} />
                          <Typography
                            variant="body1"
                            sx={{ flex: 1 }}
                            noWrap
                          >
                            {getPropertyAddress(property)}
                          </Typography>
                          <ArrowForward
                            sx={{ color: "text.secondary", fontSize: 20 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        p: 4,
                        textAlign: "center",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "12px",
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No claimed properties yet
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Search for a property and claim it as yours
                      </Typography>
                    </Box>
                  )}

                  {propertyCollections.length > 0 && (
                    <Button
                      variant="text"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate("/home-history")}
                      sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                    >
                      View home history
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid2>

            {/* My Wishlist Section */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#e91e63",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Favorite sx={{ color: "#fff", fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={600}>
                        My Wishlist
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {wishlist?.length || 0} saved{" "}
                        {(wishlist?.length as number) === 1 ? "property" : "properties"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    onClick={() => navigate("/wishlist")}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      backgroundColor: "#fce4ec",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      "&:hover": {
                        backgroundColor: "#f8bbd9",
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        View Wishlist
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        See all your saved properties
                      </Typography>
                    </Box>
                    <Chip
                      label={wishlist?.length || 0}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid2>

            {/* Plan — free/premium, billing, grants (November 2026) */}
            <Grid2 size={{ xs: 12 }}>
              <AccountPlanCard />
            </Grid2>

            {/* Email notifications (October 2026) */}
            <Grid2 size={{ xs: 12 }}>
              <EmailPreferencesCard />
            </Grid2>

            {/* Account Settings Section */}
            <Grid2 size={{ xs: 12 }}>
              <Card
                elevation={0}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "16px",
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ mb: 3 }}
                  >
                    Account Settings
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      alignItems: { xs: "stretch", sm: "center" },
                      justifyContent: "space-between",
                      p: 3,
                      borderRadius: "12px",
                      backgroundColor: "#fff3e0",
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        Signed in as
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {userEmail}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<LockReset />}
                        onClick={handleResetPassword}
                        disabled={isResetting}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          px: 3,
                        }}
                      >
                        {isResetting
                          ? "Sending..."
                          : hasPassword
                          ? "Reset Password"
                          : "Set a Password"}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Logout />}
                        onClick={handleLogout}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          px: 3,
                        }}
                      >
                        Logout
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>
        </Box>
        <Dialog open={!!serviceCta} onClose={() => setServiceCta(null)} maxWidth="xs" fullWidth>
          {serviceCta === "claim" ? (
            <>
              <DialogTitle sx={{ fontWeight: 700 }}>
                {siteConfig.vendorBiddingEnabled ? "Claim your home first" : "Request a service"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {siteConfig.vendorBiddingEnabled
                    ? "To request a service, first claim your property — then you can post the job for our verified vendors to bid on, right from your home's records."
                    : "Claim your home to send and track requests from its page. Or tell us what you need now and our team will connect you with a trusted local pro."}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1 }}>
                <Button onClick={() => setServiceCta(null)}>Not now</Button>
                {!siteConfig.vendorBiddingEnabled && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setServiceCta(null);
                      setAskOpen(true);
                    }}
                  >
                    Message our team
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    setServiceCta(null);
                    navigate("/");
                  }}
                >
                  Find my home
                </Button>
              </DialogActions>
            </>
          ) : serviceCta === "select" ? (
            <>
              <DialogTitle sx={{ fontWeight: 700 }}>Which home is this for?</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  Pick the property you'd like to request this service for.
                </DialogContentText>
                <Stack spacing={1.5}>
                  {propertyCollections.map((property: any, idx: number) => (
                    <Button
                      key={property?.propertyId?._id || idx}
                      variant="outlined"
                      startIcon={<Home />}
                      onClick={() => goToHomeRecords(property)}
                      sx={{ justifyContent: "flex-start", textTransform: "none" }}
                    >
                      {getPropertyAddress(property)}
                    </Button>
                  ))}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setServiceCta(null)}>Cancel</Button>
              </DialogActions>
            </>
          ) : null}
        </Dialog>
        <MessageDialog
          open={askOpen}
          onClose={() => setAskOpen(false)}
          title={serviceTrade && TRADE_LABELS[serviceTrade] ? `Get help with ${TRADE_LABELS[serviceTrade]}` : "Get help from a pro"}
          intro="Tell us what you need and where. Our team will connect you with a trusted local pro and reply by email."
          topic="service"
          showPhone
          messageLabel="What do you need, and where?"
          messagePlaceholder="e.g. Leaking water heater at 123 Main St, Studio City. Sometime this week."
          submitLabel="Send request"
          trade={TRADE_LABELS[serviceTrade] || undefined}
        />
      </FullPaddingWrapper>
    </Container>
  );
});

export default MyAccount;
